import React, { useState, useEffect } from "react"
import {
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  IconButton,
  Switch,
  FormControlLabel,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material"
import { ChevronLeft } from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import api from "../../services/api"
import { API_ENDPOINTS } from "../../config/api"
import { useAuth } from '../../context/AuthContext';

export default function CreateSensor() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    sensor_type_id: "",
    imei: "",
    serial_number: "",
    latitude: "",
    longitude: "",
    is_active: false,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [sensorTypes, setSensorTypes] = useState([])

  useEffect(() => {
    const fetchSensorTypes = async () => {
      try {
        const response = await api.get(API_ENDPOINTS.SENSOR_TYPE.GET_ALL)
        setSensorTypes(response.data.sensorTypes || [])
      } catch (err) {
        console.error("Error fetching sensor types:", err)
        setError("Failed to fetch sensor types. Please try again.")
      }
    }
    fetchSensorTypes()
  }, [])

  const handleSubmit = async (event, createAnother = false) => {
    event.preventDefault()
    setLoading(true)
    setError(null)
    try {
      await api.post(API_ENDPOINTS.SENSOR.CREATE, {
        ...formData,
        client_id: user.clientId
      })
      setSuccess(true)
      if (!createAnother) {
        navigate("/sensors")
      } else {
        setFormData({
          name: "",
          sensor_type_id: "",
          imei: "",
          serial_number: "",
          latitude: "",
          longitude: "",
          is_active: false,
        })
      }
    } catch (err) {
      console.error("Error creating sensor:", err)
      setError("Failed to create sensor. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (event) => {
    const { name, value, checked } = event.target
    setFormData({
      ...formData,
      [name]: name === "is_active" ? checked : value,
    })
  }

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      "& fieldset": {
        borderColor: "#E0E0E0",
      },
      "&:hover fieldset": {
        borderColor: "#1756B6",
      },
    },
  }

  const selectSx = {
    borderRadius: 8,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#E0E0E0",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "#1756B6",
    },
  }

  return (
    <Box sx={{ width: "100%", pr: 2, pt: 0, backgroundColor: "background.paper", borderRadius: 8 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <IconButton
          onClick={() => navigate("/sensors")}
          sx={{
            mr: 2,
            color: "#6C757D",
            "&:hover": {
              backgroundColor: "rgba(108, 117, 125, 0.04)",
            },
          }}
        >
          <ChevronLeft />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          Create Sensor
        </Typography>
      </Box>

      <Paper
        sx={{
          p: 2,
          borderRadius: 6,
          boxShadow: "0 5px 5px rgba(0,0,0,0.05)",
          backgroundColor: "background.primary",
        }}
      >
        <Box component="form" onSubmit={(e) => handleSubmit(e, false)}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 3 }}>
            <TextField required label="Name" name="name" value={formData.name} onChange={handleChange} sx={inputSx} />

            <FormControl required>
              <InputLabel>Sensor Type</InputLabel>
              <Select
                name="sensor_type_id"
                value={formData.sensor_type_id}
                onChange={handleChange}
                label="Sensor Type"
                sx={selectSx}
              >
                {sensorTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 3 }}>
            <TextField required label="IMEI" name="imei" value={formData.imei} onChange={handleChange} sx={inputSx} />
            <TextField
              required
              label="Serial number"
              name="serial_number"
              value={formData.serial_number}
              onChange={handleChange}
              sx={inputSx}
            />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 3 }}>
            <TextField label="Latitude" name="latitude" value={formData.latitude} onChange={handleChange} sx={inputSx} />
            <TextField label="Longitude" name="longitude" value={formData.longitude} onChange={handleChange} sx={inputSx} />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 3 }}>
            {/* <TextField label="URL" name="url" value={formData.url} onChange={handleChange} sx={inputSx} /> */}
            <FormControlLabel
              control={
                <Switch
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  sx={{
                    "& .MuiSwitch-switchBase": {
                      color: "#E0E0E0",
                      "&.Mui-checked": {
                        color: "#1967D2",
                      },
                    },
                    "& .MuiSwitch-track": {
                      backgroundColor: "#E0E0E0 !important",
                      opacity: 0.3,
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#1967D2 !important",
                      opacity: 0.3,
                    },
                  }}
                />
              }
              label={<Typography sx={{ color: "text.secondary" }}>Active Status</Typography>}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                bgcolor: "#1967D2",
                textTransform: "none",
                borderRadius: 18,
                "&:hover": { bgcolor: "#1756B6" },
              }}
            >
              {loading ? <CircularProgress size={24} /> : "Create"}
            </Button>
            <Button
              variant="outlined"
              onClick={(e) => handleSubmit(e, true)}
              disabled={loading}
              sx={{
                color: "#1967D2",
                borderColor: "#1967D2",
                textTransform: "none",
                borderRadius: 18,
                "&:hover": {
                  borderColor: "#1756B6",
                  bgcolor: "rgba(25, 103, 210, 0.04)",
                },
              }}
            >
              Create & create another
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate("/sensors")}
              sx={{
                color: "#F9826E",
                borderColor: "#F9826E",
                textTransform: "none",
                borderRadius: 18,
                "&:hover": {
                  color: "black",
                  borderColor: "#F7583D",
                  bgcolor: "#F9826E",
                },
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>
      <Snackbar open={success} autoHideDuration={6000} onClose={() => setSuccess(false)}>
        <Alert onClose={() => setSuccess(false)} severity="success" sx={{ width: "100%" }}>
          Sensor created successfully!
        </Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: "100%" }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  )
}
